import { z } from 'zod';
import {
  ChannelSchema,
  EmptyRecordSchema,
  IdSchema,
  IsoDateTimeSchema,
  LocaleSchema,
  SemverSchema,
} from './common';
import { CapabilitiesSchema } from './connector';
import { MenuSnapshotSchema, StopListSchema } from './menu';
import { OrderDraftSchema, OrderSchema } from './order';
import { VenueSummarySchema } from './tenant';
import { WidgetInstanceSchema } from './widget';

export const ApiErrorSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  details: EmptyRecordSchema.optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const GuestSessionRequestSchema = z.object({
  venueId: IdSchema,
  channel: ChannelSchema,
  telegramInitData: z.string().optional(),
  phone: z.string().min(10).optional(),
  locale: LocaleSchema.default('ru'),
});
export type GuestSessionRequest = z.infer<typeof GuestSessionRequestSchema>;

export const GuestSchema = z.object({
  id: IdSchema,
  phone: z.string().nullish(),
  telegramId: z.string().nullish(),
  locale: LocaleSchema.default('ru'),
});
export type Guest = z.infer<typeof GuestSchema>;

export const GuestSessionSchema = z.object({
  token: z.string().min(1),
  expiresAt: IsoDateTimeSchema,
  /** Для продления сессии хостом: сам токен в виджет не попадает. */
  refreshToken: z.string().min(16).optional(),
  refreshExpiresAt: IsoDateTimeSchema.optional(),
  guest: GuestSchema,
  venue: VenueSummarySchema,
  channel: ChannelSchema,
});
export type GuestSession = z.infer<typeof GuestSessionSchema>;

export const RegistryResponseSchema = z.object({
  venueId: IdSchema,
  channel: ChannelSchema,
  hostVersion: SemverSchema,
  capabilities: CapabilitiesSchema,
  widgets: z.array(WidgetInstanceSchema),
  /** Причина, по которой виджет скрыт (для админки и логов хоста). */
  skipped: z
    .array(
      z.object({
        widgetId: IdSchema,
        reason: z.enum(['channel', 'capability', 'host_version', 'disabled', 'delivery']),
        details: z.string().optional(),
      }),
    )
    .default([]),
});
export type RegistryResponse = z.infer<typeof RegistryResponseSchema>;

export const MenuResponseSchema = z.object({
  menu: MenuSnapshotSchema,
  stopList: StopListSchema,
  capabilities: CapabilitiesSchema,
});
export type MenuResponse = z.infer<typeof MenuResponseSchema>;

export const CreateOrderRequestSchema = OrderDraftSchema;
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const CreateOrderResponseSchema = z.object({
  order: OrderSchema,
  /** true, если заказ вернулся по ключу идемпотентности. */
  duplicate: z.boolean().default(false),
});
export type CreateOrderResponse = z.infer<typeof CreateOrderResponseSchema>;

export const OrderResponseSchema = z.object({
  order: OrderSchema,
});
export type OrderResponse = z.infer<typeof OrderResponseSchema>;

export const HealthResponseSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  version: z.string(),
  connectors: z.array(
    z.object({
      venueId: IdSchema,
      kind: z.string(),
      ok: z.boolean(),
      capabilities: CapabilitiesSchema,
      message: z.string().optional(),
    }),
  ),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
